import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CurrencyInput } from "@/components/ui/currency-input";

interface Deal {
  // Contract & Payment Verification
  buyer_current_balance_stated?: number;
  buyer_last_payment_date?: string;
  buyer_last_payment_amount?: number;
  buyer_late_payment_count?: number;
  buyer_late_payment_details?: string;
  buyer_past_due_amount?: number;
  
  // Property Status & Maintenance
  buyer_occupies_property?: boolean;
  buyer_tenant_info?: string;
  buyer_major_repairs_needed?: string;
  buyer_improvements_made?: string;
  buyer_tax_payment_current?: boolean;
  buyer_tax_delinquent?: boolean;
  
  // Insurance & Compliance
  buyer_has_insurance?: boolean;
  buyer_insurance_company?: string;
  buyer_insurance_policy_number?: string;
  buyer_insurance_loss_payee?: string;
  buyer_hoa_current?: boolean;
  buyer_hoa_amount?: number;
  buyer_contract_default_status?: string;
  
  // Assignment & Acknowledgment
  buyer_aware_of_assignment?: boolean;
  buyer_terms_confirmed?: boolean;
  buyer_agrees_new_payment_direction?: boolean;
  buyer_estoppel_signed?: boolean;
  buyer_estoppel_signed_date?: string;
  buyer_interview_completed_date?: string;
  buyer_interview_notes?: string;
}

interface BuyerInterviewTabProps {
  formData: Deal;
  onChange: (field: keyof Deal, value: any) => void;
}

export function BuyerInterviewTab({ formData, onChange }: BuyerInterviewTabProps) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>I. Contract & Payment Verification</CardTitle>
          <CardDescription>Verify the numbers and payment history</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyer_current_balance_stated">Current Balance (Buyer's Understanding)</Label>
              <CurrencyInput
                id="buyer_current_balance_stated"
                value={formData.buyer_current_balance_stated}
                onChange={(value) => onChange('buyer_current_balance_stated', value)}
              />
            </div>
            <div>
              <Label htmlFor="buyer_last_payment_date">Last Payment Date</Label>
              <Input
                id="buyer_last_payment_date"
                type="date"
                value={formData.buyer_last_payment_date || ''}
                onChange={(e) => onChange('buyer_last_payment_date', e.target.value)}
              />
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyer_last_payment_amount">Last Payment Amount</Label>
              <Input
                id="buyer_last_payment_amount"
                type="number"
                step="0.01"
                value={formData.buyer_last_payment_amount ?? ''}
                onChange={(e) => onChange('buyer_last_payment_amount', parseFloat(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label htmlFor="buyer_late_payment_count">Number of Late Payments</Label>
              <Input
                id="buyer_late_payment_count"
                type="number"
                value={formData.buyer_late_payment_count ?? ''}
                onChange={(e) => onChange('buyer_late_payment_count', parseInt(e.target.value) || 0)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="buyer_late_payment_details">Late Payment Details</Label>
            <Textarea
              id="buyer_late_payment_details"
              value={formData.buyer_late_payment_details || ''}
              onChange={(e) => onChange('buyer_late_payment_details', e.target.value)}
              placeholder="When were payments late and why?"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="buyer_past_due_amount">Past Due Amount</Label>
            <Input
              id="buyer_past_due_amount"
              type="number"
              step="0.01"
              value={formData.buyer_past_due_amount ?? ''}
              onChange={(e) => onChange('buyer_past_due_amount', parseFloat(e.target.value) || 0)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>II. Property Status & Maintenance</CardTitle>
          <CardDescription>Current condition and occupancy</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="buyer_occupies_property"
              checked={formData.buyer_occupies_property || false}
              onCheckedChange={(checked) => onChange('buyer_occupies_property', checked)}
            />
            <Label htmlFor="buyer_occupies_property">Buyer currently occupies the property</Label>
          </div>
          
          <div>
            <Label htmlFor="buyer_tenant_info">Tenant Information (if applicable)</Label>
            <Textarea
              id="buyer_tenant_info"
              value={formData.buyer_tenant_info || ''}
              onChange={(e) => onChange('buyer_tenant_info', e.target.value)}
              placeholder="Who is the tenant? Are they current on rent?"
              rows={2}
            />
          </div>
          
          <div>
            <Label htmlFor="buyer_major_repairs_needed">Major Repairs or Issues</Label>
            <Textarea
              id="buyer_major_repairs_needed"
              value={formData.buyer_major_repairs_needed || ''}
              onChange={(e) => onChange('buyer_major_repairs_needed', e.target.value)}
              placeholder="Any maintenance issues or needed repairs?"
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="buyer_improvements_made">Improvements Made</Label>
            <Textarea
              id="buyer_improvements_made"
              value={formData.buyer_improvements_made || ''}
              onChange={(e) => onChange('buyer_improvements_made', e.target.value)}
              placeholder="Significant improvements since taking possession"
              rows={2}
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="buyer_tax_payment_current"
                checked={formData.buyer_tax_payment_current || false}
                onCheckedChange={(checked) => onChange('buyer_tax_payment_current', checked)}
              />
              <Label htmlFor="buyer_tax_payment_current">Property taxes are current</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="buyer_tax_delinquent"
                checked={formData.buyer_tax_delinquent || false}
                onCheckedChange={(checked) => onChange('buyer_tax_delinquent', checked)}
              />
              <Label htmlFor="buyer_tax_delinquent">Property taxes are delinquent</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>III. Insurance & Compliance</CardTitle>
          <CardDescription>Policy details and compliance status</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="buyer_has_insurance"
              checked={formData.buyer_has_insurance || false}
              onCheckedChange={(checked) => onChange('buyer_has_insurance', checked)}
            />
            <Label htmlFor="buyer_has_insurance">Buyer has hazard insurance policy</Label>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="buyer_insurance_company">Insurance Company</Label>
              <Input
                id="buyer_insurance_company"
                value={formData.buyer_insurance_company || ''}
                onChange={(e) => onChange('buyer_insurance_company', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="buyer_insurance_policy_number">Policy Number</Label>
              <Input
                id="buyer_insurance_policy_number"
                value={formData.buyer_insurance_policy_number || ''}
                onChange={(e) => onChange('buyer_insurance_policy_number', e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="buyer_insurance_loss_payee">Loss Payee / Additional Insured</Label>
            <Input
              id="buyer_insurance_loss_payee"
              value={formData.buyer_insurance_loss_payee || ''}
              onChange={(e) => onChange('buyer_insurance_loss_payee', e.target.value)}
              placeholder="Should be current seller/vendor"
            />
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="buyer_hoa_current"
                checked={formData.buyer_hoa_current || false}
                onCheckedChange={(checked) => onChange('buyer_hoa_current', checked)}
              />
              <Label htmlFor="buyer_hoa_current">HOA payments are current</Label>
            </div>
            <div>
              <Label htmlFor="buyer_hoa_amount">HOA Amount</Label>
              <Input
                id="buyer_hoa_amount"
                type="number"
                step="0.01"
                value={formData.buyer_hoa_amount ?? ''}
                onChange={(e) => onChange('buyer_hoa_amount', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="buyer_contract_default_status">Contract Default Status</Label>
            <Textarea
              id="buyer_contract_default_status"
              value={formData.buyer_contract_default_status || ''}
              onChange={(e) => onChange('buyer_contract_default_status', e.target.value)}
              placeholder="Any defaults? (e.g., illegal activity, unapproved changes)"
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>IV. Assignment & Acknowledgment</CardTitle>
          <CardDescription>Assignment awareness and estoppel certification</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="buyer_aware_of_assignment"
              checked={formData.buyer_aware_of_assignment || false}
              onCheckedChange={(checked) => onChange('buyer_aware_of_assignment', checked)}
            />
            <Label htmlFor="buyer_aware_of_assignment">Buyer is aware seller is assigning their interest</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="buyer_terms_confirmed"
              checked={formData.buyer_terms_confirmed || false}
              onCheckedChange={(checked) => onChange('buyer_terms_confirmed', checked)}
            />
            <Label htmlFor="buyer_terms_confirmed">Buyer understands terms will NOT change</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="buyer_agrees_new_payment_direction"
              checked={formData.buyer_agrees_new_payment_direction || false}
              onCheckedChange={(checked) => onChange('buyer_agrees_new_payment_direction', checked)}
            />
            <Label htmlFor="buyer_agrees_new_payment_direction">Buyer agrees to direct future payments to new investor</Label>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="buyer_estoppel_signed"
                checked={formData.buyer_estoppel_signed || false}
                onCheckedChange={(checked) => onChange('buyer_estoppel_signed', checked)}
              />
              <Label htmlFor="buyer_estoppel_signed">Estoppel Certificate signed</Label>
            </div>
            <div>
              <Label htmlFor="buyer_estoppel_signed_date">Estoppel Signed Date</Label>
              <Input
                id="buyer_estoppel_signed_date"
                type="date"
                value={formData.buyer_estoppel_signed_date || ''}
                onChange={(e) => onChange('buyer_estoppel_signed_date', e.target.value)}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="buyer_interview_completed_date">Interview Completed Date</Label>
            <Input
              id="buyer_interview_completed_date"
              type="datetime-local"
              value={formData.buyer_interview_completed_date || ''}
              onChange={(e) => onChange('buyer_interview_completed_date', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="buyer_interview_notes">Interview Notes</Label>
            <Textarea
              id="buyer_interview_notes"
              value={formData.buyer_interview_notes || ''}
              onChange={(e) => onChange('buyer_interview_notes', e.target.value)}
              placeholder="Additional notes from the buyer interview"
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
